import { styled } from "@mui/material/styles";
import currency from "currency.js";
import { Transaction as TransactionType } from "../types";
import { ReactComponent as TransferIcon } from "../assets/icons/transactions/transfer.svg";
import { ReactComponent as CardIcon } from "../assets/icons/transactions/card.svg";
import { ReactComponent as BonusIcon } from "../assets/icons/transactions/bonus.svg";
import { SectionFlexColumn, SectionFlexRow } from "../components/General";
import T from "../components/Typography";
import { colors } from "../styles";
import { formatCurrency } from "../helpers/currency";

export const TRANSACTION_TYPE = {
  HOLD: "hold",
  HOLD_RELEASE: "hold_release",
  DEPOSIT: "deposit",
};

export const ACH_TRANSACTION_TYPE = {
  ACH_HOLD: "hold",
  ACH_HOLD_RELEASE: "hold_release",
};

const { HOLD, HOLD_RELEASE, DEPOSIT } = TRANSACTION_TYPE;

const Label = styled("div")({
  justifyContent: "center",
  alignItems: "center",
  padding: 4,
  paddingLeft: 15,
  paddingRight: 15,
  borderRadius: 30,
  marginBottom: 5,
  borderColor: colors.black,
  borderStyle: "solid",
});

const TransactionTypeLabel = ({
  transaction,
}: {
  transaction: TransactionType;
}) => {
  const renderAchLabel = () => {
    const isAchHold =
      transaction.annotation_ach_type === ACH_TRANSACTION_TYPE.ACH_HOLD;
    return (
      <div>
        <Label
          style={{
            borderWidth: isAchHold ? 0.5 : 0,
          }}
        >
          <T variant="smMed">{isAchHold ? "Hold" : ""}</T>
        </Label>
      </div>
    );
  };

  const renderNonAchLabel = () => {
    return (
      <>
        <Label
          style={{
            borderWidth: transaction.tp_type === HOLD_RELEASE ? 0.5 : 0,
            backgroundColor:
              transaction.tp_type === HOLD ? colors.yellow : "none",
          }}
        >
          <T variant="smMed">
            {transaction.tp_type === HOLD && "Pending"}
            {transaction.tp_type === HOLD_RELEASE && "Hold"}
          </T>
        </Label>
      </>
    );
  };

  return (
    <div>
      {transaction.annotation_ach_type === null
        ? renderNonAchLabel()
        : renderAchLabel()}
    </div>
  );
};

const TransactionIcon = ({ transaction }: { transaction: TransactionType }) => {
  if (transaction.is_payout) {
    return <BonusIcon />;
  }

  // for deposit transactions
  if (transaction?.tp_type === DEPOSIT) {
    return <TransferIcon />;
  }

  // for ach transactions
  if (
    transaction?.annotation_ach_type &&
    transaction?.annotation_ach_type !== null
  ) {
    return <TransferIcon />;
  }

  // return card transaction as default
  return <CardIcon />;
};

const Transaction = ({
  transaction,
  showBorder = true,
}: {
  transaction: TransactionType;
  showBorder: boolean;
}) => {
  const amount = formatCurrency(transaction.amount, 2);
  return (
    <SectionFlexRow
      style={{
        height: "90px",
        borderBottom: showBorder ? `1px solid ${colors.gray_200}` : "none",
        padding: "15px 0",
      }}
      className="w-100"
    >
      <div style={{ alignSelf: "flex-start" }}>
        <TransactionIcon transaction={transaction} />
      </div>
      <SectionFlexColumn style={{ marginLeft: 12, width: "100%" }}>
        <SectionFlexRow style={{ justifyContent: "space-between" }}>
          <T variant="lgMed" sx={{ flexWrap: "wrap" }}>
            {transaction.description_readable}
          </T>
          <TransactionTypeLabel transaction={transaction} />
        </SectionFlexRow>
        <SectionFlexRow
          style={{ marginTop: 6, justifyContent: "space-between" }}
        >
          <T variant="mdReg" color={colors.gray_400}>
            {transaction.timestamp_readable}
          </T>
          <T
            variant="lgMed"
            color={
              Number(transaction.amount) > 0 ? colors.dark_green : colors.black
            }
          >
            {amount}
          </T>
        </SectionFlexRow>
      </SectionFlexColumn>
    </SectionFlexRow>
  );
};

export default Transaction;
